import { useState, useEffect } from 'react'
import { Modal } from '../ui/Modal'
import type { VisualTemplateDetail } from '../../lib/api'
import type { TemplateCreateRequest, TemplateUpdateRequest } from '../../lib/api'

const DEFAULT_CONFIG_JSON = '{\n  "layout": "default",\n  "style": "professional"\n}'

export interface TemplateFormDataCreate {
  id: string
  name: string
  category: string
  channel: string
  config: Record<string, unknown>
}

export interface TemplateFormDataUpdate {
  name: string
  config: Record<string, unknown>
  is_active: boolean
}

interface TemplateFormProps {
  isOpen: boolean
  onClose: () => void
  onSubmitCreate?: (data: TemplateCreateRequest) => Promise<void>
  onSubmitUpdate?: (id: string, data: TemplateUpdateRequest) => Promise<void>
  initialData?: VisualTemplateDetail | null
  isLoading?: boolean
}

export function TemplateForm({
  isOpen,
  onClose,
  onSubmitCreate,
  onSubmitUpdate,
  initialData,
  isLoading,
}: TemplateFormProps) {
  const isEdit = !!onSubmitUpdate
  const isLoadingDetail = isEdit && initialData === undefined

  const [id, setId] = useState('')
  const [name, setName] = useState('')
  const [category, setCategory] = useState('')
  const [channel, setChannel] = useState('')
  const [configJson, setConfigJson] = useState(DEFAULT_CONFIG_JSON)
  const [configError, setConfigError] = useState<string | null>(null)
  const [idError, setIdError] = useState<string | null>(null)
  const [isActive, setIsActive] = useState(true)

  useEffect(() => {
    if (initialData) {
      setName(initialData.name)
      setCategory(initialData.category)
      setChannel(initialData.channel ?? '')
      setConfigJson(
        initialData.config && Object.keys(initialData.config).length > 0
          ? JSON.stringify(initialData.config, null, 2)
          : DEFAULT_CONFIG_JSON
      )
      setIsActive(initialData.is_active)
    } else if (!isEdit) {
      setId('')
      setName('')
      setCategory('')
      setChannel('')
      setConfigJson(DEFAULT_CONFIG_JSON)
      setIsActive(true)
      setConfigError(null)
      setIdError(null)
    }
  }, [initialData, isOpen, isEdit])

  const parseConfig = (): Record<string, unknown> | null => {
    setConfigError(null)
    try {
      const parsed = JSON.parse(configJson) as unknown
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setConfigError('Config debe ser un objeto JSON')
        return null
      }
      return parsed as Record<string, unknown>
    } catch {
      setConfigError('JSON inválido')
      return null
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const config = parseConfig()
    if (config === null) return

    if (isEdit && initialData) {
      if (onSubmitUpdate) {
        await onSubmitUpdate(initialData.id, { name, config, is_active: isActive })
        onClose()
      }
    } else {
      const tid = id?.trim() ?? ''
      if (!tid) {
        setIdError('ID es obligatorio')
        return
      }
      if (onSubmitCreate) {
        await onSubmitCreate({
          id: tid,
          name: name.trim(),
          category: category.trim(),
          channel: channel.trim() || undefined,
          config,
        })
        onClose()
      }
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Editar plantilla' : 'Nueva plantilla'}
      size="lg"
    >
      {isLoadingDetail ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-emerald-600" />
        </div>
      ) : (
      <form onSubmit={handleSubmit} className="space-y-4">
        {!isEdit && (
          <div>
            <label htmlFor="template-id" className="label">
              ID *
            </label>
            <input
              id="template-id"
              type="text"
              className={`input font-mono ${idError ? 'border-red-500' : ''}`}
              placeholder="ej. linkedin-card-1"
              value={id}
              onChange={(e) => { setId(e.target.value); setIdError(null) }}
              required
            />
            {idError && <p className="mt-1 text-sm text-red-600">{idError}</p>}
          </div>
        )}

        <div>
          <label htmlFor="template-name" className="label">
            Nombre *
          </label>
          <input
            id="template-name"
            type="text"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        {!isEdit && (
          <>
            <div>
              <label htmlFor="template-category" className="label">
                Categoría *
              </label>
              <input
                id="template-category"
                type="text"
                className="input"
                placeholder="ej. social, blog"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                required
              />
            </div>
            <div>
              <label htmlFor="template-channel" className="label">
                Canal (opcional)
              </label>
              <input
                id="template-channel"
                type="text"
                className="input"
                placeholder="ej. linkedin, instagram"
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
              />
            </div>
          </>
        )}

        <div>
          <label htmlFor="template-config" className="label">
            Config (JSON) *
          </label>
          <textarea
            id="template-config"
            className={`input font-mono text-sm min-h-[120px] ${configError ? 'border-red-500' : ''}`}
            value={configJson}
            onChange={(e) => setConfigJson(e.target.value)}
            spellCheck={false}
          />
          {configError && (
            <p className="mt-1 text-sm text-red-600">{configError}</p>
          )}
        </div>

        {isEdit && (
          <div className="flex items-center gap-2">
            <input
              id="template-active"
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="rounded border-slate-300"
            />
            <label htmlFor="template-active" className="label mb-0">
              Activa
            </label>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancelar
          </button>
          <button type="submit" disabled={isLoading} className="btn btn-primary">
            {isLoading ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear plantilla'}
          </button>
        </div>
      </form>
      )}
    </Modal>
  )
}
